import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PiggyBank, Loader2, Plus, Minus, ShoppingCart, Trash2 } from "lucide-react";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OssRisparmio() {
  const { token, activeOss } = useOssAuth();
  const canAccess = Boolean(activeOss?.responsabile);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [struttura, setStruttura] = useState(null);
  const [carrello, setCarrello] = useState([]);
  const [query, setQuery] = useState("");
  const [invio, setInvio] = useState(false);
  const [risparmio, setRisparmio] = useState({ risparmio_totale: 0, num_ordini: 0 });
  const [ordini, setOrdini] = useState([]);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : null),
    [token]
  );

  const load = useCallback(async () => {
    if (!headers || !canAccess) return;
    setLoading(true);
    try {
      const [listinoRes, ctxRes, rispRes, ordRes] = await Promise.all([
        axios.get(`${API_URL}/api/price-list`),
        axios.get(`${API_URL}/api/oss/me/struttura-contesto`, { headers }),
        axios.get(`${API_URL}/api/oss/me/ordini/risparmio`, { headers }),
        axios.get(`${API_URL}/api/oss/me/ordini`, { headers }),
      ]);
      const mapped = (listinoRes.data.products || []).map((p) => ({
        id: p.default_sku || p.name,
        nome: p.name,
        categoria: p.category,
        sku: p.tiers?.[0]?.sku || p.default_sku,
        packQty: p.tiers?.[0]?.pack_qty || 1,
        packUnit: p.tiers?.[0]?.pack_unit || "PZ",
        prezzo: p.tiers?.[0]?.unit_price || p.base_price,
        discountPct: p.tiers?.[0]?.discount_pct || 0,
        vatRate: p.tiers?.[0]?.vat_rate || 22,
      }));
      setProducts(mapped);
      setStruttura(ctxRes.data);
      setRisparmio(rispRes.data || {});
      setOrdini(ordRes.data.ordini || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento Risparmio insieme");
    } finally {
      setLoading(false);
    }
  }, [headers, canAccess]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canAccess) {
    return <Navigate to="/oss" replace />;
  }

  const filtered = products.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.nome || "").toLowerCase().includes(q) ||
      (p.categoria || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q)
    );
  });

  const addToCart = (p) => {
    setCarrello((prev) => {
      const existing = prev.find((x) => x.sku === p.sku);
      if (existing) {
        return prev.map((x) =>
          x.sku === p.sku ? { ...x, quantita: x.quantita + 1 } : x
        );
      }
      return [...prev, { ...p, quantita: 1 }];
    });
  };

  const changeQty = (sku, delta) => {
    setCarrello((prev) =>
      prev
        .map((x) => (x.sku === sku ? { ...x, quantita: x.quantita + delta } : x))
        .filter((x) => x.quantita > 0)
    );
  };

  const totale = carrello.reduce((acc, i) => acc + i.prezzo * i.quantita, 0);

  const inviaOrdine = async () => {
    if (!carrello.length || !struttura) {
      toast.error("Carrello vuoto");
      return;
    }
    setInvio(true);
    try {
      const ordineData = {
        struttura_id: struttura.struttura_id,
        struttura_nome: struttura.nome || "Struttura",
        cliente_email: struttura.email || activeOss?.email || "ordini@affidya.it",
        cliente_telefono: struttura.telefono || "",
        referente_nome: `${activeOss?.nome || ""} ${activeOss?.cognome || ""}`.trim(),
        referente_telefono: activeOss?.telefono || "",
        ragione_sociale: struttura.nome || "",
        partita_iva: struttura.partita_iva || "",
        codice_fiscale: struttura.codice_fiscale || "",
        pec: struttura.pec || "",
        codice_sdi: struttura.codice_sdi || "",
        indirizzo: struttura.indirizzo || "",
        cap: struttura.cap || "",
        citta: struttura.citta || "",
        provincia: struttura.provincia || "",
        spedizione_indirizzo: struttura.indirizzo || "",
        spedizione_cap: struttura.cap || "",
        spedizione_citta: struttura.citta || "",
        spedizione_provincia: struttura.provincia || "",
        items: carrello.map((item) => ({
          sku: item.sku || "",
          nome: item.nome || "",
          categoria: item.categoria || "altro",
          pack_qty: item.packQty || 1,
          pack_unit: item.packUnit || "PZ",
          prezzo: item.prezzo || 0,
          discount_pct: item.discountPct || 0,
          quantita: item.quantita || 1,
          iva: item.vatRate || 22,
        })),
        note: "Ordine da area OSS Responsabile",
      };
      const { data } = await axios.post(`${API_URL}/api/oss/me/ordini`, ordineData, {
        headers,
      });
      if (data.success) {
        toast.success(`Ordine ${data.ordine_id} confermato · €${Number(data.totale || 0).toFixed(2)}`);
        setCarrello([]);
        load();
      } else {
        toast.error("Errore nella creazione dell'ordine");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio ordine");
    } finally {
      setInvio(false);
    }
  };

  return (
    <div className="space-y-4">
      <OssSectionPageHeader
        sectionKey="risparmio"
        title="Risparmio insieme"
        subtitle="Listino e ordini per la struttura"
        toolbar={
          <Input
            placeholder="Cerca prodotto..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-11 bg-white/80"
          />
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500">Risparmio accumulato</p>
            <p className="text-2xl font-bold text-emerald-700">
              € {(risparmio.risparmio_totale || 0).toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500">Ordini effettuati</p>
            <p className="text-2xl font-bold text-slate-800">{risparmio.num_ordini || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500">Carrello</p>
            <p className="text-2xl font-bold text-sky-700">€ {totale.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {filtered.slice(0, 80).map((p) => (
                <div
                  key={p.id + p.sku}
                  className="bg-white border border-slate-100 rounded-xl p-3 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{p.nome}</p>
                    <p className="text-xs text-slate-500">
                      {p.categoria} · {p.sku} · pack {p.packQty} {p.packUnit}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-emerald-700">€ {Number(p.prezzo).toFixed(2)}</p>
                    {p.discountPct > 0 && (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                        -{p.discountPct}%
                      </Badge>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => addToCart(p)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Card className="h-fit sticky top-20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShoppingCart className="w-4 h-4" />
                Carrello ({carrello.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {carrello.length === 0 ? (
                <p className="text-sm text-slate-500">Nessun prodotto</p>
              ) : (
                carrello.map((item) => (
                  <div key={item.sku} className="flex items-center gap-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{item.nome}</p>
                      <p className="text-xs text-slate-500">
                        € {item.prezzo.toFixed(2)} × {item.quantita}
                      </p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => changeQty(item.sku, -1)}>
                      <Minus className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => changeQty(item.sku, 1)}>
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="font-semibold">Totale</span>
                <span className="font-bold text-lg">€ {totale.toFixed(2)}</span>
              </div>
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={!carrello.length || invio}
                onClick={inviaOrdine}
              >
                {invio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PiggyBank className="w-4 h-4 mr-2" />}
                Invia ordine
              </Button>
              {carrello.length > 0 && (
                <Button variant="ghost" size="sm" className="w-full text-slate-500" onClick={() => setCarrello([])}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Svuota
                </Button>
              )}
              {ordini.length > 0 && (
                <div className="pt-3 border-t space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Ultimi ordini</p>
                  {ordini.slice(0, 5).map((o) => (
                    <div key={o.ordine_id} className="text-xs text-slate-600 flex justify-between gap-2">
                      <span className="truncate">{o.ordine_id}</span>
                      <span className="shrink-0 font-medium">€ {Number(o.totale || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
